import { useEffect, useState } from 'react';
import client from '../api/client';

const SEGMENTS = [ /* SEGMENTS_v2 */
  { v:'WARM',           label:'Warm (users + opted-in)' },
  { v:'USERS',          label:'Existing users only' },
  { v:'OPTED_IN',       label:'Opted-in leads only' },
  { v:'COLD',           label:'Cold list (all)' },
  { v:'DAYCARE_TX:100', label:'— Childcare TX · first 100' },
  { v:'DAYCARE_TX:500', label:'— Childcare TX · first 500' },
  { v:'DAYCARE_TX',     label:'— Childcare TX · all 6,654' },
  { v:'DEALERS_TX:100', label:'— Dealers TX · first 100' },
  { v:'DEALERS_TX:500', label:'— Dealers TX · first 500' },
  { v:'DEALERS_TX',     label:'— Dealers TX · all 14,398' },
  { v:'DEALERS_FL:100', label:'— Dealers FL · first 100' },
  { v:'DEALERS_FL:500', label:'— Dealers FL · first 500' },
  { v:'DEALERS_FL',     label:'— Dealers FL · all 10,767' },
  { v:'DEALERS',        label:'— Dealers TX + FL · all 25,165' },
  { v:'GROUP_5',        label:'— TEST · operator only (1)' }, /* SEGMENTS_v3 */
  { v:'GROUP_4',        label:'— Insurance TX · P&C agents (17,362)' },
  { v:'INSURANCE_TX:100',  label:'— Insurance TX · first 100' },
  { v:'INSURANCE_TX:500',  label:'— Insurance TX · first 500' },
  { v:'INSURANCE_FL:100',  label:'— Insurance FL · first 100' },
  { v:'INSURANCE_FL:500',  label:'— Insurance FL · first 500' },
  { v:'ALL',            label:'Everyone (32,072)' },
];

export default function Marketing() {
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  /* MKT_LAUNCH_UI_v1 */
  const [launch, setLaunch] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [launching, setLaunching] = useState(false);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [segment, setSegment] = useState('WARM');
  const [body, setBody] = useState('');
  const [creating, setCreating] = useState(false);

  const [importText, setImportText] = useState('');
  const [importTier, setImportTier] = useState('COLD');
  const [importing, setImporting] = useState(false);

  const [testEmail, setTestEmail] = useState('');

  // template editor
  const [editTpl, setEditTpl] = useState(null); // {id?, name, subject, body_html}

  // campaign detail
  const [openCampaign, setOpenCampaign] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      client.get('/admin/marketing/contacts/summary').then(r=>setSummary(r.data)).catch(()=>{}),
      client.get('/admin/marketing/campaigns').then(r=>setCampaigns(r.data||[])).catch(()=>{}),
      client.get('/admin/marketing/templates').then(r=>setTemplates(r.data||[])).catch(()=>{}),
    ]).finally(()=>setLoading(false));
  };
  useEffect(load, []);

  /* MKT_LAUNCH_UI_v1 - poll while any campaign is SENDING */
  useEffect(() => {
    const live = (campaigns||[]).filter(c => c.status === 'SENDING');
    if (!live.length) return;
    let stop = false;
    const tick = async () => {
      for (const c of live) {
        try {
          const r = await client.get(`/admin/marketing/campaigns/${c.id}/sends?limit=2000`);
          const rows = r.data.rows || [];
          const sent = rows.filter(x => x.sent_at).length;
          const queued = rows.filter(x => x.status === 'QUEUED').length;
          if (!stop) setProgress(p => ({ ...p, [c.id]: { sent, queued, total: rows.length } }));
        } catch(e) {}
      }
      if (!stop) { try { const cr = await client.get('/admin/marketing/campaigns'); setCampaigns(cr.data||[]); } catch(e){} }
    };
    const h = setInterval(tick, 10000);
    tick();
    return () => { stop = true; clearInterval(h); };
  }, [campaigns.map(c=>c.id+c.status).join(',')]);

  const flash = (m, isErr) => { if(isErr){setErr(m);setMsg('');}else{setMsg(m);setErr('');} setTimeout(()=>{setMsg('');setErr('');},4000); };

  const doImport = async () => {
    if (importing) return;
    const lines = importText.split('\n').map(l=>l.trim()).filter(Boolean);
    if (!lines.length) return flash('Paste at least one contact.', true);
    const rows = lines.map(l => { const p=l.split(',').map(x=>x.trim()); return { email:p[0], business_name:p[1]||null, phone:p[2]||null }; });
    setImporting(true);
    try {
      const r = await client.post('/admin/marketing/contacts/import', { rows, consent_tier: importTier, source:'admin-paste' });
      flash(`Imported ${r.data.inserted}, skipped ${r.data.skipped} (of ${r.data.received}).`);
      setImportText(''); load();
    } catch(e){ flash(e.response?.data || 'Import failed', true); } finally { setImporting(false); }
  };

  const applyTemplate = (id) => {
    const t = templates.find(x=>String(x.id)===String(id));
    if (t){ setSubject(t.subject||''); setBody(t.body_html||''); flash(`Loaded template "${t.name}".`); }
  };

  const createCampaign = async () => {
    if (creating) return;
    if (!name.trim() || !subject.trim()) return flash('Name and subject are required.', true);
    setCreating(true);
    try {
      await client.post('/admin/marketing/campaigns', { name, subject, body_template: body, segment, channel:'EMAIL' });
      flash('Campaign created.'); setName(''); setSubject(''); setBody(''); load();
    } catch(e){ flash(e.response?.data || 'Create failed', true); } finally { setCreating(false); }
  };

  const previewRecipients = async (id) => {
    try { const r = await client.get(`/admin/marketing/campaigns/${id}/preview`); flash(`Eligible recipients: ${r.data.eligibleRecipients}`); }
    catch(e){ flash('Preview failed', true); }
  };
  const sendTest = async (id) => {
    if (!testEmail.trim()) return flash('Enter a test email address first.', true);
    try { const r = await client.post(`/admin/marketing/campaigns/${id}/send?test=true&testEmail=${encodeURIComponent(testEmail)}`); flash(`Test send: ${r.data.result} to ${testEmail}`); }
    catch(e){ flash(e.response?.data || 'Test send failed', true); }
  };
  const openLaunch = async (c) => {
    setConfirmText(''); setLaunch({ ...c, eligible: null, note: null });
    try {
      const r = await client.get(`/admin/marketing/campaigns/${c.id}/preview`);
      let note = null;
      const tpl = templates.find(t => t.subject === c.subject);
      if (tpl && tpl.notes) note = tpl.notes;
      setLaunch(prev => prev && prev.id === c.id
        ? { ...prev, eligible: r.data.eligibleRecipients, note } : prev);
    } catch(e) {
      setLaunch(prev => prev ? { ...prev, eligible: 'unknown' } : prev);
    }
  };

  const sendNow = async (camp) => { /* DIRECT_SEND_v1 */
    if (launching) return;
    setLaunching(true);
    try {
      const r = await client.post(
        `/admin/marketing/campaigns/${camp.id}/send?confirm=${encodeURIComponent(String(camp.name).trim())}`);
      flash(`Launched. ${r.data.queued} queued of ${r.data.totalRecipients}. Sending in the background.`);
      load();
    } catch (e) {
      const d = e.response?.data;
      flash(d?.message || d?.error || 'Launch failed', true);
    } finally { setLaunching(false); }
  };

  const doLaunch = async () => {
    if (!launch) return;
    if (confirmText.trim().toLowerCase() !== String(launch.name).trim().toLowerCase()) return;
    setLaunching(true);
    try {
      const r = await client.post(
        `/admin/marketing/campaigns/${launch.id}/send?confirm=${encodeURIComponent(confirmText.trim())}`);
      flash(`Launched. ${r.data.queued} queued of ${r.data.totalRecipients}. Sending in the background.`);
      setLaunch(null); setConfirmText(''); load();
    } catch(e) {
      const d = e.response?.data;
      flash(d?.message || d?.error || 'Launch failed', true);
    } finally { setLaunching(false); }
  };

  const sendReal = async (id) => {
    try { const r = await client.post(`/admin/marketing/campaigns/${id}/send`); flash(`Launched ${r.data.queued}.`); load(); }
    catch(e){ flash(e.response?.data || 'Send failed', true); }
  };

  const toggleDetail = async (id) => {
    if (openCampaign === id) { setOpenCampaign(null); setDetail(null); return; }
    setOpenCampaign(id); setDetail(null);
    try { const r = await client.get(`/admin/marketing/campaigns/${id}/sends`); setDetail(r.data); }
    catch(e){ flash('Could not load detail', true); }
  };

  // templates
  const saveTemplate = async () => {
    if (!editTpl.name?.trim()) return flash('Template name required.', true);
    try {
      if (editTpl.id) await client.patch(`/admin/marketing/templates/${editTpl.id}`, { name:editTpl.name, subject:editTpl.subject, body_html:editTpl.body_html });
      else await client.post('/admin/marketing/templates', { name:editTpl.name, subject:editTpl.subject, body_html:editTpl.body_html });
      flash('Template saved.'); setEditTpl(null); load();
    } catch(e){ flash(e.response?.data || 'Save failed', true); }
  };
  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await client.delete(`/admin/marketing/templates/${id}`); flash('Template deleted.'); load(); }
    catch(e){ flash(e.response?.data || 'Delete failed', true); }
  };

  const card = { background:'#fff', borderRadius:12, border:'1px solid #eee', padding:20, marginBottom:20 };
  const lbl = { color:'#555', fontSize:12, fontWeight:600, textTransform:'uppercase', marginBottom:6, display:'block' };
  const inp = { width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:7, fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:'inherit' };
  const btn = { padding:'9px 16px', background:'#1a5cff', color:'#fff', border:'none', borderRadius:7, cursor:'pointer', fontSize:14, fontWeight:600 };
  const btnGhost = { ...btn, background:'#f0f4ff', color:'#1a5cff', border:'1px solid #cdddff' };
  const btnDanger = { ...btn, background:'#fff0f0', color:'#c0392b', border:'1px solid #f0c0c0' };

  return (
    <div>
      <h1 style={{ color:'#000', fontSize:22, fontWeight:700, margin:'0 0 8px', fontFamily:'Georgia,serif' }}>Marketing Campaigns</h1>
      <p style={{ color:'#555', margin:'0 0 24px', fontSize:14 }}>Import contacts, manage templates, compose campaigns, and track delivery. Email only. Unsubscribes and bounces are suppressed automatically.</p>

      {msg && <div style={{ background:'#f0fff6', border:'1px solid #b2dfdb', borderRadius:7, padding:'10px 14px', color:'#0a8a40', fontSize:14, marginBottom:16 }}>{msg}</div>}
      {err && <div style={{ background:'#fff5f5', border:'1px solid #f0c0c0', borderRadius:7, padding:'10px 14px', color:'#c0392b', fontSize:14, marginBottom:16 }}>{err}</div>}

      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        {[{k:'total',label:'Total contacts'},{k:'emailable',label:'Emailable'},{k:'suppressed',label:'Suppressed'}].map(s=>(
          <div key={s.k} style={{ flex:'1 1 140px', background:'#f7f9ff', borderRadius:10, padding:'14px 16px', border:'1px solid #eef2ff' }}>
            <div style={{ color:'#555', fontSize:12 }}>{s.label}</div>
            <div style={{ color:'#000', fontSize:24, fontWeight:700 }}>{summary?(summary[s.k]??0):'—'}</div>
          </div>
        ))}
      </div>

      {/* IMPORT */}
      <div style={card}>
        <div style={{ color:'#000', fontSize:15, fontWeight:700, marginBottom:12 }}>Import contacts</div>
        <label style={lbl}>One per line: <span style={{ fontWeight:400, textTransform:'none' }}>email,business name,phone</span></label>
        <textarea style={{ ...inp, minHeight:80, fontFamily:'monospace' }} value={importText} onChange={e=>setImportText(e.target.value)} placeholder={"jane@dealer.com,Jane Auto LLC,2145551234"} />
        <div style={{ display:'flex', gap:12, alignItems:'flex-end', marginTop:12 }}>
          <div style={{ flex:'0 0 240px' }}><label style={lbl}>Consent tier</label>
            <select style={inp} value={importTier} onChange={e=>setImportTier(e.target.value)}>
              <option value="USER">Existing user (warm)</option><option value="OPTED_IN">Opted-in lead (warm)</option><option value="COLD">Cold list</option>
            </select>
          </div>
          <button style={btn} onClick={doImport} disabled={importing}>{importing?'Importing...':'Import'}</button>
        </div>
      </div>

      {/* TEMPLATES */}
      <div style={card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <div style={{ color:'#000', fontSize:15, fontWeight:700 }}>Templates</div>
          <button style={btnGhost} onClick={()=>setEditTpl({ name:'', subject:'', body_html:'' })}>+ New template</button>
        </div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ borderBottom:'1px solid #eee' }}>{['Name','Subject','Actions'].map(h=><th key={h} style={{ color:'#555', fontSize:11, fontWeight:500, textAlign:'left', padding:'10px 12px', textTransform:'uppercase' }}>{h}</th>)}</tr></thead>
          <tbody>
            {templates.length===0?<tr><td colSpan={3} style={{ padding:18, textAlign:'center', color:'#888' }}>No templates.</td></tr>
            :templates.map(t=>(
              <tr key={t.id} style={{ borderBottom:'1px solid #f5f5f5' }}>
                <td style={{ padding:'10px 12px', fontSize:14, color:'#000', fontWeight:500 }}>{t.name}</td>
                <td style={{ padding:'10px 12px', fontSize:13, color:'#555' }}>{t.subject}</td>
                <td style={{ padding:'10px 12px' }}>
                  <div style={{ display:'flex', gap:6 }}>
                    <button style={{ ...btnGhost, padding:'6px 10px', fontSize:12 }} onClick={()=>{ /* TPL_VIEW_BTN_v1 */ const w = window.open('', '_blank'); if (!w) { flash('Popup blocked — allow popups for this site', true); return; } w.document.write(t.body_html || '<p style="font-family:sans-serif;padding:40px">This template has no HTML body.</p>'); w.document.title = t.name || 'Template preview'; w.document.close(); }}>View</button>
                    <button style={{ ...btnGhost, padding:'6px 10px', fontSize:12 }} onClick={()=>setEditTpl({ id:t.id, name:t.name, subject:t.subject||'', body_html:t.body_html||'' })}>Edit</button>
                    <button style={{ ...btnDanger, padding:'6px 10px', fontSize:12 }} onClick={()=>deleteTemplate(t.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* COMPOSER */}
      <div style={card}>
        <div style={{ color:'#000', fontSize:15, fontWeight:700, marginBottom:12 }}>Create campaign</div>
        <div style={{ display:'flex', gap:12, marginBottom:12 }}>
          <div style={{ flex:1 }}><label style={lbl}>Campaign name</label><input style={inp} value={name} onChange={e=>setName(e.target.value)} placeholder="June activation invite" /></div>
          <div style={{ flex:'0 0 220px' }}><label style={lbl}>Segment</label><select style={inp} value={segment} onChange={e=>setSegment(e.target.value)}>{SEGMENTS.map(s=><option key={s.v} value={s.v}>{s.label}</option>)}</select></div>
          <div style={{ flex:'0 0 220px' }}><label style={lbl}>Load template</label><select style={inp} defaultValue="" onChange={e=>{ if(e.target.value) applyTemplate(e.target.value); }}><option value="">— choose —</option>{templates.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
        </div>
        <label style={lbl}>Subject</label>
        <input style={{ ...inp, marginBottom:12 }} value={subject} onChange={e=>setSubject(e.target.value)} placeholder="Activate your OurEsign account — first month free" />
        <label style={lbl}>Body (HTML — {'{{activation_url}}'} and {'{{business_name}}'} merge fields)</label>
        <textarea style={{ ...inp, minHeight:140, fontFamily:'monospace' }} value={body} onChange={e=>setBody(e.target.value)} placeholder="<p>Hi {{business_name}}, ...</p>" />
        <div style={{ marginTop:12 }}><button style={btn} onClick={createCampaign} disabled={creating}>{creating?'Creating...':'Create campaign'}</button></div>
      </div>

      <div style={{ ...card, display:'flex', gap:12, alignItems:'flex-end' }}>
        <div style={{ flex:1 }}><label style={lbl}>Test send address</label><input style={inp} value={testEmail} onChange={e=>setTestEmail(e.target.value)} placeholder="you@yourcompany.com" /></div>
      </div>

      {/* CAMPAIGNS */}
      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #eee', overflow:'hidden' }}>
        <div style={{ padding:'14px 16px', borderBottom:'1px solid #eee', color:'#000', fontSize:15, fontWeight:700 }}>Campaigns</div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ borderBottom:'1px solid #eee' }}>{['Name','Segment','Status','Sent','Opened','Clicked','Bounced','Activated','Signed Up','Actions'].map(h=><th key={h} style={{ color:'#555', fontSize:11, fontWeight:500, textAlign:'left', padding:'12px 16px', textTransform:'uppercase' }}>{h}</th>)}</tr></thead>
          <tbody>
            {loading?<tr><td colSpan={10} style={{ padding:24, textAlign:'center', color:'#555' }}>Loading...</td></tr>
            :campaigns.length===0?<tr><td colSpan={10} style={{ padding:24, textAlign:'center', color:'#888' }}>No campaigns yet.</td></tr>
            :campaigns.map(c=>(
              <>
              <tr key={c.id} style={{ borderBottom:'1px solid #f5f5f5', cursor:'pointer' }} onClick={()=>toggleDetail(c.id)}>
                <td style={{ padding:'12px 16px' }}><div style={{ color:'#000', fontSize:14, fontWeight:500 }}>{openCampaign===c.id?'▾ ':'▸ '}{c.name}</div><div style={{ color:'#888', fontSize:12 }}>{c.subject}</div></td>
                <td style={{ padding:'12px 16px', color:'#333', fontSize:13 }}>{c.segment}</td>
                <td style={{ padding:'12px 16px' }}><span style={{ background:c.status==='SENT'?'#e6f5ef':c.status==='SENDING'?'#fef3da':'#f0f0f0', color:c.status==='SENT'?'#0f6e56':c.status==='SENDING'?'#854f0b':'#555', padding:'3px 8px', borderRadius:4, fontSize:12, fontWeight:600 }}>{c.status}</span></td>
                <td style={{ padding:'12px 16px', color:'#333', fontSize:14 }}>{c.sent_count??0}</td>
                <td style={{ padding:'12px 16px', color:'#333', fontSize:14 }}>{c.opened_count??0}</td>
                <td style={{ padding:'12px 16px', color:'#333', fontSize:14 }}>{c.clicked_count??0}</td>
                <td style={{ padding:'12px 16px', color:'#333', fontSize:14 }}>{c.bounced_count??0}</td>
                <td style={{ padding:'12px 16px', color:'#0f6e56', fontSize:14, fontWeight:600 }}>{c.activated_count??0}</td>
                <td style={{ padding:'12px 16px', color:'#1a5cff', fontSize:14, fontWeight:700 }}>{c.signedup_count??0}</td>
                <td style={{ padding:'12px 16px' }} onClick={e=>e.stopPropagation()}>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <button style={{ ...btnGhost, padding:'6px 10px', fontSize:12 }} onClick={()=>previewRecipients(c.id)}>Preview</button>
                    <button style={{ ...btnGhost, padding:'6px 10px', fontSize:12 }} onClick={()=>sendTest(c.id)}>Test</button>
                    <button style={{ ...btn, padding:'6px 10px', fontSize:12 }} disabled={launching} onClick={()=>sendNow(c)}>Send</button>
                    {c.status==='SENDING' && progress[c.id] && (
                      <span style={{ fontSize:11, color:'#555', marginLeft:8 }}>
                        {progress[c.id].sent} sent &middot; {progress[c.id].queued} queued
                      </span>
                    )}
                  </div>
                </td>
              </tr>
              {openCampaign===c.id && (
                <tr><td colSpan={10} style={{ background:'#fafbff', padding:'16px 20px' }}>
                  {!detail?<div style={{ color:'#888', fontSize:13 }}>Loading detail…</div>:(
                    <>
                      {/* ENGAGEMENT_v1 */}
                      {(()=>{ 
                        const rows = detail.rows||[];
                        const cc = detail.counters||{};
                        const n = k => Number(cc[k]||0);
                        const total = n('total_recipients')||rows.length;
                        const sent = n('sent_count');
                        const opened = rows.filter(r=>r.opened_at).length || n('opened_count');
                        const clicked = rows.filter(r=>r.clicked_at).length || n('clicked_count');
                        const signed = n('activated_count') || rows.filter(r=>r.activated_at).length;
                        const signedUp = n('mkt_signed_up_count');
                        const bounced = rows.filter(r=>r.status==='BOUNCED'||r.status==='FAILED').length || n('bounced_count');
                        const pct = v => sent>0 ? Math.round(v*1000/sent)/10 + '%' : '—';
                        const S = [
                          ['Generated', total, null, '#0f1117'],
                          ['Sent', sent, pct(sent), '#0e7a56'],
                          ['Opened', opened, pct(opened), '#1a5cff'],
                          ['Clicked', clicked, pct(clicked), '#1a5cff'],
                          ['Engaged', signed, pct(signed), '#0e7a56'],
                          ['Signed up', signedUp, pct(signedUp), '#0e7a56'],
                          ['Bounced', bounced, pct(bounced), bounced>0?'#a32b2b':'#6b7280'],
                        ];
                        const stage = r => r.activated_at ? ['ENGAGED','#0e7a56','#fff']
                          : r.clicked_at ? ['CLICKED','#eef3ff','#1a5cff']
                          : r.opened_at ? ['OPENED','#f1f5f9','#475569']
                          : (r.status==='BOUNCED'||r.status==='FAILED') ? ['BOUNCED','#fdeaea','#a32b2b']
                          : r.sent_at ? ['SENT','#f8fafc','#6b7280']
                          : [r.status||'QUEUED','#f8fafc','#6b7280'];
                        const trail = r => {
                          const t=[]; if(r.sent_at)t.push('Sent'); if(r.delivered_at)t.push('Delivered');
                          if(r.opened_at)t.push('Opened'); if(r.clicked_at)t.push('Clicked');
                          if(r.activated_at)t.push('Engaged');
                          if(r.status==='BOUNCED'||r.status==='FAILED')t.push('Bounced'+(r.error?' — '+String(r.error).slice(0,60):''));
                          return t.length?t.join(' · '):'Queued';
                        };
                        const csv = () => {
                          const head=['email','business_name','contact_name','source','status','sent_at','opened_at','clicked_at','activated_at','error'];
                          const esc=v=>'"'+String(v==null?'':v).replace(/"/g,'""')+'"';
                          const body=rows.map(r=>head.map(h=>esc(r[h])).join(',')).join('\n');
                          const blob=new Blob([head.join(',')+'\n'+body],{type:'text/csv'});
                          const a=document.createElement('a'); a.href=URL.createObjectURL(blob);
                          a.download='campaign-'+(detail.campaignId||'export')+'.csv'; a.click();
                        };
                        return (<>
                          <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16 }}>
                            {S.map(([lab,val,p2,col])=>(
                              <div key={lab} style={{ flex:'1 1 100px', minWidth:100, border:'1px solid #e5e7eb', borderRadius:10, padding:'13px 14px', background:'#fff', textAlign:'center' }}>
                                <div style={{ fontSize:23, fontWeight:600, color:col, letterSpacing:'-0.5px' }}>{val}</div>
                                <div style={{ fontSize:10.5, fontWeight:600, letterSpacing:'.08em', textTransform:'uppercase', color:'#6b7280', paddingTop:5 }}>{lab}</div>
                                {p2 && <div style={{ fontSize:12, color:'#9ca3af', paddingTop:2 }}>{p2}</div>}
                              </div>))}
                          </div>
                          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                              <thead><tr style={{ background:'#f6f7f9' }}>
                                {['Recipient','Status','Trail'].map(h=><th key={h} style={{ textAlign:'left', padding:'11px 14px', color:'#6b7280', fontSize:10.5, fontWeight:600, letterSpacing:'.08em', textTransform:'uppercase' }}>{h}</th>)}
                              </tr></thead>
                              <tbody>
                                {rows.length===0?<tr><td colSpan={3} style={{ padding:14, color:'#9ca3af' }}>No sends recorded yet.</td></tr>
                                :rows.map(r=>{ const [lab,bg,fg]=stage(r); return (
                                  <tr key={r.id} style={{ borderTop:'1px solid #f1f2f4' }}>
                                    <td style={{ padding:'12px 14px' }}>
                                      <div style={{ color:'#0f1117', fontSize:13.5 }}>{r.email}</div>
                                      <div style={{ color:'#9ca3af', fontSize:12.5, paddingTop:2 }}>{[r.business_name,r.contact_name].filter(Boolean).join(' · ')||'—'}</div>
                                    </td>
                                    <td style={{ padding:'12px 14px' }}>
                                      <span style={{ fontSize:10.5, fontWeight:600, letterSpacing:'.05em', background:bg, color:fg, borderRadius:5, padding:'5px 9px', whiteSpace:'nowrap' }}>{lab}</span>
                                    </td>
                                    <td style={{ padding:'12px 14px', color:'#6b7280', fontSize:12.5 }}>{trail(r)}</td>
                                  </tr>); })}
                              </tbody>
                            </table>
                          </div>
                          <div style={{ paddingTop:12 }}>
                            <button style={{ ...btnGhost, padding:'8px 14px', fontSize:12.5 }} onClick={csv}>↓ Export CSV</button>
                          </div>
                        </>);
                      })()}
                    </>
                  )}
                </td></tr>
              )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* TEMPLATE EDITOR MODAL */}
      {editTpl && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:20 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, width:560, maxWidth:'100%', maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ margin:'0 0 16px', fontSize:17, fontFamily:'Georgia,serif', color:'#000' }}>{editTpl.id?'Edit template':'New template'}</h3>
            <label style={lbl}>Name</label>
            <input style={{ ...inp, marginBottom:12 }} value={editTpl.name} onChange={e=>setEditTpl({ ...editTpl, name:e.target.value })} />
            <label style={lbl}>Subject</label>
            <input style={{ ...inp, marginBottom:12 }} value={editTpl.subject} onChange={e=>setEditTpl({ ...editTpl, subject:e.target.value })} />
            <label style={lbl}>Body (HTML)</label>
            <textarea style={{ ...inp, minHeight:180, fontFamily:'monospace', marginBottom:16 }} value={editTpl.body_html} onChange={e=>setEditTpl({ ...editTpl, body_html:e.target.value })} />
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button style={{ ...btn, background:'#eee', color:'#555' }} onClick={()=>setEditTpl(null)}>Cancel</button>
              <button style={btn} onClick={saveTemplate}>Save template</button>
            </div>
          </div>
        </div>
      )}
      {launch && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex',
                      alignItems:'center', justifyContent:'center', zIndex:9999 }}
             onClick={()=>!launching && setLaunch(null)}>
          <div onClick={e=>e.stopPropagation()}
               style={{ background:'#fff', borderRadius:12, border:'1px solid #e3e6ea',
                        width:'100%', maxWidth:440, padding:'20px 24px', margin:16 }}>
            <h3 style={{ margin:'0 0 4px', fontSize:16, fontWeight:600 }}>Launch campaign</h3>
            <p style={{ margin:'0 0 16px', fontSize:13, color:'#666' }}>
              Sending happens in the background. You can close this window.</p>

            <div style={{ background:'#f4f6f9', borderRadius:8, padding:'12px 14px', marginBottom:14 }}>
              <table style={{ width:'100%', fontSize:13 }}>
                <tbody>
                  <tr><td style={{ color:'#666', padding:'3px 0' }}>Campaign</td>
                      <td style={{ textAlign:'right', fontWeight:600 }}>{launch.name}</td></tr>
                  <tr><td style={{ color:'#666', padding:'3px 0' }}>Segment</td>
                      <td style={{ textAlign:'right' }}>{launch.segment}</td></tr>
                  <tr><td style={{ color:'#666', padding:'3px 0' }}>Eligible now</td>
                      <td style={{ textAlign:'right', fontWeight:600 }}>
                        {launch.eligible === null ? 'checking...' : launch.eligible}</td></tr>
                </tbody>
              </table>
            </div>

            {launch.note && (
              <div style={{ background:'#fff8e8', borderRadius:8, padding:'10px 12px',
                            marginBottom:16, fontSize:12, lineHeight:1.5, color:'#854f0b' }}>
                {launch.note}
              </div>
            )}

            <label style={{ display:'block', fontSize:13, color:'#666', marginBottom:6 }}>
              Type the campaign name to confirm</label>
            <input value={confirmText} onChange={e=>setConfirmText(e.target.value)}
                   placeholder={launch.name}
                   style={{ width:'100%', padding:'9px 11px', border:'1px solid #ddd',
                            borderRadius:8, fontSize:14, boxSizing:'border-box', marginBottom:16 }} />

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button style={{ ...btnGhost, fontSize:13 }} disabled={launching}
                      onClick={()=>setLaunch(null)}>Cancel</button>
              <button style={{ ...btn, fontSize:13,
                               opacity: confirmText.trim().toLowerCase() === String(launch.name).trim().toLowerCase() ? 1 : 0.45,
                               cursor: confirmText.trim().toLowerCase() === String(launch.name).trim().toLowerCase() ? 'pointer' : 'not-allowed' }}
                      disabled={launching || confirmText.trim().toLowerCase() !== String(launch.name).trim().toLowerCase()}
                      onClick={doLaunch}>
                {launching ? 'Launching...' : 'Launch'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
